import {CHAIN_CONFIG, type EvmNetworkKey} from '#/config/evm'

type RpcRequestArgs = {
  chainId?: number
  chainKey?: EvmNetworkKey
  method: string
  params?: unknown[]
}

type RpcCacheEntry = {
  url: string
  ts: number
}

const RPC_CACHE_TTL = 10 * 60 * 1000
const rpcCache = new Map<number, RpcCacheEntry>()

const isDev =
  typeof __DEV__ !== 'undefined'
    ? __DEV__
    : typeof process !== 'undefined'
      ? process.env.NODE_ENV !== 'production'
      : false

const shouldRetryStatus = (status: number) =>
  status === 403 || status === 429 || (status >= 500 && status <= 599)

const isRetryableError = (err: unknown) => {
  if (!err) return true
  if (err instanceof TypeError) return true
  const message = String((err as Error)?.message || '').toLowerCase()
  return (
    message.includes('failed to fetch') ||
    message.includes('network request failed') ||
    message.includes('cors') ||
    message.includes('timeout') ||
    message.includes('http_')
  )
}

const logDebug = (...args: any[]) => {
  if (!isDev) return
  // eslint-disable-next-line no-console
  console.debug('[rpc]', ...args)
}

const getChainConfig = (chainId?: number, chainKey?: EvmNetworkKey) => {
  if (chainKey) return CHAIN_CONFIG[chainKey]
  if (!chainId) return null
  return (
    Object.values(CHAIN_CONFIG).find(cfg => cfg.chainId === chainId) || null
  )
}

export async function rpcRequestWithFallback({
  chainId,
  chainKey,
  method,
  params,
}: RpcRequestArgs) {
  const config = getChainConfig(chainId, chainKey)
  if (!config) {
    throw new Error('unsupported_chain')
  }

  const cacheKey = config.chainId
  const now = Date.now()
  const cached = rpcCache.get(cacheKey)
  const urls = config.rpcUrls.filter(Boolean)
  const candidates = cached && now - cached.ts < RPC_CACHE_TTL
    ? [cached.url, ...urls.filter(url => url !== cached.url)]
    : urls

  let lastError: unknown = null

  for (const url of candidates) {
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: Math.floor(Math.random() * 1_000_000),
          method,
          params: params ?? [],
        }),
      })

      if (!response.ok) {
        const err = new Error(`http_${response.status}`)
        if (shouldRetryStatus(response.status)) {
          throw err
        }
        throw err
      }

      const payload = (await response.json()) as {
        result?: unknown
        error?: {message?: string}
      }

      if (payload?.error) {
        throw new Error(payload.error.message || 'rpc_error')
      }

      rpcCache.set(cacheKey, {url, ts: now})
      return payload?.result
    } catch (err) {
      lastError = err
      logDebug('request_failed', {
        chain: config.name,
        url,
        method,
        error: (err as Error)?.message || err,
      })
      if (!isRetryableError(err)) {
        break
      }
    }
  }

  throw lastError || new Error('rpc_unavailable')
}
